import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { PageHeader } from '@/components/layout/PageHeader';
import { SocialTabNav, type SocialTabItem } from './SocialTabNav';
import type { PlatformDefinition } from '../config/platformCapabilities';

interface SocialPlatformShellProps {
  platform: PlatformDefinition;
  activeTab: string;
  tabs: SocialTabItem[];
  children: ReactNode;
  actions?: ReactNode;
}

export function SocialPlatformShell({
  platform,
  activeTab,
  tabs,
  children,
  actions,
}: SocialPlatformShellProps) {
  const tabLabel = tabs.find((t) => t.id === activeTab)?.label ?? activeTab;

  return (
    <div className="flex min-h-0 flex-col">
      <PageHeader
        title={platform.label}
        description={`Dedicated ${platform.label} workspace — platform-specific tools only`}
        breadcrumbs={[
          { label: 'Social', to: '/social' },
          { label: 'Platforms', to: '/social/connect' },
          { label: platform.label, to: `/social/platforms/${platform.id}` },
          { label: tabLabel },
        ]}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            {actions}
            <span
              className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold text-white"
              style={{ backgroundColor: platform.brandColor }}
            >
              {platform.apiPlatform}
            </span>
            <Link
              to={`/social/connect/wizard/${platform.id}`}
              className="text-sm text-primary-600 hover:underline dark:text-primary-400"
            >
              Manage connection
            </Link>
          </div>
        }
      />
      <SocialTabNav tabs={tabs} className="mb-4" />
      <div className="min-h-0 flex-1">{children}</div>
    </div>
  );
}
